/**
 * Who may SEE the deliverables section.
 *
 * The read half of the matrix had no consumer anywhere: every check in the app asked whether
 * deliverables could be *written*, so the DRAFT matrix marked the section `-` for eleven of
 * fourteen roles and all eleven could read it, contents included. Hiding is a different
 * question from editing and needs its own answer. (MR !489 review, batch 2 change 2.3.)
 *
 * **Its own module, deliberately.** The write-side sibling
 * (`canFallBackToEditingDeliverables`) lives in `useFormPermissions`, and cohesion argues for
 * putting this beside it. Twenty-eight test suites mock that module, so every new named
 * export there is a trap: any suite whose mock omits it breaks the moment a component calls
 * it, with a `not a function` that points at the component rather than at the mock. This is a
 * pure function rather than a hook, and the sidebar consumes it without otherwise touching
 * `useFormPermissions`, so a module of its own costs nothing and removes the trap.
 */

import { fieldPermissionService } from '../services/auth/fieldPermissionService';

import type { AppRole } from '../types/auth';

export interface DeliverablesVisibilityContext {
  userRoles: readonly string[] | null | undefined;
  requestStage?: string | null;
  requestId?: string | null;
  userEmail?: string | null;
}

/**
 * Routed through `fieldPermissionService` rather than comparing role strings here, so the
 * Partner Contributor elevation and the stage aliasing stay in one place.
 *
 * Both conditions are checked. `isHidden` is the `-` cell and `canRead` is the positive
 * grant; a matrix that ever disagreed with itself should hide rather than show.
 *
 * **Testing note:** `src/setupTests.ts` mocks `fieldPermissionService` open for the whole
 * client suite (`canRead: true, isHidden: false` for every field), so a test that does not
 * override that mock locally cannot observe this gate at all and will pass while proving
 * nothing.
 */
export const canReadDeliverablesAtStage = (
  context: DeliverablesVisibilityContext,
): boolean => {
  const access = fieldPermissionService.checkFieldAccessSync({
    userRoles: (context.userRoles ?? []) as AppRole[],
    section: 'Deliverables',
    field: 'Deliverables',
    requestStage: context.requestStage ?? undefined,
    requestId: context.requestId ?? undefined,
    userEmail: context.userEmail ?? undefined,
  });

  return access.canRead && !access.isHidden;
};
